import jsPDF from "jspdf";
import type { TemplateDefinition, TemplateData, RepeatableRow } from "./types";
import { TEMPLATES } from "./templates-index";
import { MODULE_MAP } from "./modules";
import { getOfficer, getCompletionStats } from "./storage";

const NAVY: [number, number, number] = [27, 58, 107];
const GOLD: [number, number, number] = [200, 168, 75];
const TEXT: [number, number, number] = [26, 26, 26];
const TEXT_SECONDARY: [number, number, number] = [85, 85, 85];
const LIGHT_GRAY: [number, number, number] = [240, 240, 240];

function getPrefillValue(field: { prefill?: string; prefillFrom?: string }): string {
  if (field.prefill) return field.prefill;
  if (field.prefillFrom) {
    const officer = getOfficer();
    if (!officer) return "";
    switch (field.prefillFrom) {
      case "name": return officer.fullName;
      case "department": return officer.department;
      case "badge": return officer.badgeNumber;
      case "rank": return officer.rank;
      case "district": return officer.patrolDistrict;
    }
  }
  return "";
}

function fieldValue(data: TemplateData, fieldId: string): string {
  const v = data.fields[fieldId];
  if (typeof v === "string" && v) return v;
  return "";
}

function repeatableRows(data: TemplateData, groupId: string, prepopulated?: RepeatableRow[]): RepeatableRow[] {
  return data.repeatables[groupId] ?? prepopulated ?? [];
}

function checklistChecked(data: TemplateData, groupId: string): Record<string, boolean> {
  return data.checklists[groupId] ?? {};
}

function checklistNotes(data: TemplateData, groupId: string): Record<string, string> {
  return data.checklistNotes[groupId] ?? {};
}

// ── Single Template PDF ──

export function exportTemplatePDF(template: TemplateDefinition, data: TemplateData): void {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 50;
  const contentWidth = pageWidth - margin * 2;
  let y = 0;

  const officer = getOfficer();

  // ── Header bar ──
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, pageWidth, 60, "F");

  // Gold accent line
  doc.setFillColor(...GOLD);
  doc.rect(0, 60, pageWidth, 3, "F");

  // Wordmark
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("SENTINEL", margin, 28);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...GOLD);
  doc.text("LEP-C Professional Template System", margin, 42);

  // Officer info top-right
  if (officer) {
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(9);
    doc.text(officer.fullName, pageWidth - margin, 25, { align: "right" });
    doc.setFontSize(7);
    doc.setTextColor(200, 200, 200);
    doc.text(officer.department, pageWidth - margin, 37, { align: "right" });
  }

  // Credential badge
  doc.setFillColor(...NAVY);
  doc.roundedRect(pageWidth - margin - 60, 45, 55, 14, 2, 2, "F");
  doc.setTextColor(...GOLD);
  doc.setFontSize(7);
  doc.setFont("helvetica", "bold");
  doc.text("LEP-C", pageWidth - margin - 32, 54, { align: "center" });

  y = 85;

  // Template title
  doc.setTextColor(...NAVY);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  const titleLines = doc.splitTextToSize(template.name, contentWidth);
  doc.text(titleLines, margin, y);
  y += titleLines.length * 18 + 4;

  // Stage + Module
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...TEXT_SECONDARY);
  const mod = MODULE_MAP[template.module];
  doc.text(`${template.stage}  |  Module ${template.module}: ${mod?.name ?? ""}`, margin, y);
  y += 14;

  doc.setFontSize(8);
  doc.text(`Pillars: ${template.pillars}`, margin, y);
  y += 20;

  // Why this tool exists
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...GOLD);
  doc.text("WHY THIS TOOL EXISTS", margin, y);
  y += 12;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...TEXT);
  const whyLines = doc.splitTextToSize(template.whyExists, contentWidth);
  doc.text(whyLines, margin, y);
  y += whyLines.length * 12 + 16;

  // Sections
  for (const section of template.sections) {
    if (y > pageHeight - 100) {
      doc.addPage();
      y = margin;
    }

    // Section title
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...NAVY);
    const secLines = doc.splitTextToSize(section.title, contentWidth);
    doc.text(secLines, margin, y);
    y += secLines.length * 14 + 4;

    // Section description
    if (section.description) {
      doc.setFont("helvetica", "italic");
      doc.setFontSize(8);
      doc.setTextColor(...TEXT_SECONDARY);
      const descLines = doc.splitTextToSize(section.description, contentWidth);
      doc.text(descLines, margin, y);
      y += descLines.length * 11 + 4;
    }

    // Fields
    if (section.fields) {
      for (const f of section.fields) {
        if (y > pageHeight - 60) { doc.addPage(); y = margin; }

        const value = fieldValue(data, f.id) || getPrefillValue(f);
        if (f.type === "checkbox") {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(9);
          doc.setTextColor(...TEXT);
          const checkMark = value === "true" ? "[X]" : "[ ]";
          const lblLines = doc.splitTextToSize(`${checkMark} ${f.label}`, contentWidth);
          doc.text(lblLines, margin, y);
          y += lblLines.length * 12 + 4;
        } else if (f.type === "toggle") {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(9);
          doc.setTextColor(...TEXT);
          const toggleVal = value === "true" ? "Yes" : "Not applicable";
          doc.text(`${f.label}: ${toggleVal}`, margin, y);
          y += 12;
        } else if (f.type === "radio") {
          doc.setFont("helvetica", "bold");
          doc.setFontSize(8);
          doc.setTextColor(...TEXT_SECONDARY);
          doc.text(f.label, margin, y);
          y += 11;
          doc.setFont("helvetica", "normal");
          doc.setFontSize(9);
          doc.setTextColor(...TEXT);
          const selected = f.options?.find((o) => o.value === value);
          doc.text(`Selected: ${selected?.label ?? "None"}`, margin + 10, y);
          y += 12;
        } else if (f.type === "slider") {
          doc.setFont("helvetica", "bold");
          doc.setFontSize(8);
          doc.setTextColor(...TEXT_SECONDARY);
          doc.text(`${f.label}: ${value || "Not set"}`, margin, y);
          y += 12;
        } else {
          // Label
          doc.setFont("helvetica", "bold");
          doc.setFontSize(8);
          doc.setTextColor(...TEXT_SECONDARY);
          doc.text(f.label, margin, y);
          y += 11;

          // Value
          doc.setFont("helvetica", "normal");
          doc.setFontSize(9);
          doc.setTextColor(...TEXT);
          if (value) {
            const valLines = doc.splitTextToSize(value, contentWidth - 10);
            doc.text(valLines, margin + 10, y);
            y += valLines.length * 12 + 4;
          } else {
            doc.setTextColor(180, 180, 180);
            doc.text("(Not completed)", margin + 10, y);
            y += 12;
          }
          y += 4;
        }
      }
    }

    // Repeatable
    if (section.repeatable) {
      const rows = repeatableRows(data, section.repeatable.id, section.repeatable.prepopulated);
      for (let i = 0; i < rows.length; i++) {
        if (y > pageHeight - 80) { doc.addPage(); y = margin; }

        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.setTextColor(...NAVY);
        doc.text(`${section.repeatable.label} ${i + 1}`, margin, y);
        y += 12;

        for (const f of section.repeatable.fields) {
          if (f.id === "color") continue;
          if (y > pageHeight - 50) { doc.addPage(); y = margin; }

          const val = (rows[i][f.id] as string) ?? "";
          doc.setFont("helvetica", "bold");
          doc.setFontSize(8);
          doc.setTextColor(...TEXT_SECONDARY);
          doc.text(`${f.label}:`, margin + 10, y);
          y += 10;

          doc.setFont("helvetica", "normal");
          doc.setFontSize(9);
          doc.setTextColor(...TEXT);
          if (val) {
            const valLines = doc.splitTextToSize(val, contentWidth - 20);
            doc.text(valLines, margin + 20, y);
            y += valLines.length * 12;
          } else {
            doc.setTextColor(180, 180, 180);
            doc.text("(Not completed)", margin + 20, y);
            y += 12;
          }
        }
        y += 6;
      }
    }

    // Checklist
    if (section.checklist) {
      const checked = checklistChecked(data, section.checklist.id);
      const notes = checklistNotes(data, section.checklist.id);
      for (const item of section.checklist.items) {
        if (y > pageHeight - 50) { doc.addPage(); y = margin; }

        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(...TEXT);
        const mark = checked[item.id] ? "[X]" : "[ ]";
        const itemLines = doc.splitTextToSize(`${mark} ${item.label}`, contentWidth - 10);
        doc.text(itemLines, margin + 10, y);
        y += itemLines.length * 12;

        if (checked[item.id] && notes[item.id]) {
          doc.setFont("helvetica", "italic");
          doc.setFontSize(8);
          doc.setTextColor(...TEXT_SECONDARY);
          const noteLines = doc.splitTextToSize(`Notes: ${notes[item.id]}`, contentWidth - 30);
          doc.text(noteLines, margin + 20, y);
          y += noteLines.length * 11;
        }
        y += 2;
      }
    }

    y += 8;
  }

  // ── Footer on every page ──
  const pageCount = doc.getNumberOfPages();
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    doc.setFillColor(...NAVY);
    doc.rect(0, pageHeight - 25, pageWidth, 25, "F");
    doc.setTextColor(200, 200, 200);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.text(
      "© Sentinel Certification System | S-LELSC L1 | LEP-C | Unauthorized reproduction prohibited.",
      margin,
      pageHeight - 10,
    );
    doc.text(`Page ${p} of ${pageCount}`, pageWidth - margin, pageHeight - 10, { align: "right" });
  }

  doc.save(`LEP-C_${template.id}_${template.name.replace(/\s+/g, "_")}.pdf`);
}

// ── Portfolio PDF (all templates summary) ──

export function exportPortfolioPDF(): void {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 50;
  const contentWidth = pageWidth - margin * 2;
  let y = 0;

  const officer = getOfficer();
  const stats = getCompletionStats();

  // Header
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, pageWidth, 60, "F");
  doc.setFillColor(...GOLD);
  doc.rect(0, 60, pageWidth, 3, "F");

  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("SENTINEL", margin, 28);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...GOLD);
  doc.text("LEP-C Professional Portfolio Summary", margin, 42);

  if (officer) {
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(9);
    doc.text(officer.fullName, pageWidth - margin, 25, { align: "right" });
    doc.setFontSize(7);
    doc.setTextColor(200, 200, 200);
    doc.text(officer.department, pageWidth - margin, 37, { align: "right" });
  }

  y = 85;

  // Title
  doc.setTextColor(...NAVY);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("Professional Portfolio Summary", margin, y);
  y += 24;

  // Progress
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...TEXT_SECONDARY);
  doc.text(`Overall Progress: ${stats.complete} of ${stats.total} templates complete`, margin, y);
  y += 14;
  doc.text(`In Progress: ${stats.inProgress}  |  Not Started: ${stats.notStarted}`, margin, y);
  y += 20;

  // Each template
  for (const template of TEMPLATES) {
    if (y > pageHeight - 80) { doc.addPage(); y = margin; }

    // Module separator
    const mod = MODULE_MAP[template.module];
    if (template.number === 1) {
      doc.setFillColor(...LIGHT_GRAY);
      doc.rect(margin - 5, y - 10, contentWidth + 10, 18, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(...NAVY);
      doc.text(`MODULE ${template.module}: ${mod?.name ?? ""}`, margin, y);
      y += 24;
    }

    // Template entry
    const data = JSON.parse(localStorage.getItem("sentinel-lepc-state") ?? "{}");
    const tplData = data.templates?.[template.id];
    const status = tplData?.status ?? "not_started";
    const statusIcon = status === "complete" ? "[COMPLETE]" : status === "in_progress" ? "[IN PROGRESS]" : "[NOT STARTED]";
    const statusColor: [number, number, number] = status === "complete" ? [45, 125, 70] : status === "in_progress" ? [204, 122, 0] : [85, 85, 85];

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...NAVY);
    doc.text(`Template ${template.number}: ${template.name}`, margin, y);
    y += 12;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...statusColor);
    doc.text(statusIcon, margin, y);
    y += 12;

    // Key field summary (first 3 non-empty fields)
    if (tplData?.fields) {
      let count = 0;
      for (const section of template.sections) {
        if (count >= 3) break;
        if (section.fields) {
          for (const f of section.fields) {
            if (count >= 3) break;
            const val = typeof tplData.fields[f.id] === "string" ? tplData.fields[f.id] as string : "";
            if (val && val.trim()) {
              doc.setFont("helvetica", "bold");
              doc.setFontSize(7);
              doc.setTextColor(...TEXT_SECONDARY);
              doc.text(`${f.label}:`, margin + 10, y);
              y += 9;
              doc.setFont("helvetica", "normal");
              doc.setFontSize(8);
              doc.setTextColor(...TEXT);
              const valLines = doc.splitTextToSize(val.substring(0, 200), contentWidth - 20);
              doc.text(valLines, margin + 20, y);
              y += valLines.length * 10 + 4;
              count++;
            }
          }
        }
      }
    }

    y += 8;
  }

  // Footer
  const pageCount = doc.getNumberOfPages();
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    doc.setFillColor(...NAVY);
    doc.rect(0, pageHeight - 25, pageWidth, 25, "F");
    doc.setTextColor(200, 200, 200);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.text(
      "© Sentinel Certification System | S-LELSC L1 | LEP-C | Unauthorized reproduction prohibited.",
      margin,
      pageHeight - 10,
    );
    doc.text(`Page ${p} of ${pageCount}`, pageWidth - margin, pageHeight - 10, { align: "right" });
  }

  doc.save("LEP_C_Professional_Portfolio.pdf");
}
